import axios from 'axios';

// In Vite dev (DEV=true) the Vite proxy at /api forwards to the backend on :3010
// (and strips /api). In production, the backend itself serves both frontend and API
// on the same origin — so requests go directly to /auth, /customers, ...
const API_BASE = import.meta.env.DEV ? '/api' : '';

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' }
});

let accessToken = null;
let refreshing = null;
let onAuthFailedHandler = null;

export function setAccessToken(token) {
  accessToken = token;
}

export function getAccessToken() {
  return accessToken;
}

/**
 * Register a callback that fires when refresh definitively fails. The AuthProvider
 * uses this to clear its React state so ProtectedRoute can route to /login via the
 * client-side router — no hard reload, no redirect loop.
 */
export function onAuthFailed(handler) {
  onAuthFailedHandler = handler;
}

api.interceptors.request.use((cfg) => {
  if (accessToken) cfg.headers.Authorization = `Bearer ${accessToken}`;
  return cfg;
});

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const original = err.config || {};
    const status = err.response?.status;
    const isRefreshCall = original.url?.includes('/auth/refresh');
    const isLoginCall   = original.url?.includes('/auth/login') || original.url?.includes('/auth/mfa-verify');

    // The refresh endpoint itself returning 401 = session is gone server-side.
    // Definitively log the user out.
    if (status === 401 && isRefreshCall) {
      accessToken = null;
      if (onAuthFailedHandler) onAuthFailedHandler();
      return Promise.reject(err);
    }

    // Login endpoint 401 is "wrong credentials", not a session-loss event.
    if (status === 401 && isLoginCall) return Promise.reject(err);

    // Already retried OR no token — terminal 401, clear auth state.
    if (status === 401 && (original._retry || !accessToken)) {
      accessToken = null;
      if (onAuthFailedHandler) onAuthFailedHandler();
      return Promise.reject(err);
    }

    // First 401 with a token: try a one-shot refresh + retry.
    if (status === 401 && !original._retry && accessToken) {
      original._retry = true;
      try {
        if (!refreshing) {
          refreshing = api.post('/auth/refresh')
            .then((r) => { accessToken = r.data.access_token; return accessToken; })
            .finally(() => { refreshing = null; });
        }
        const newToken = await refreshing;
        original.headers = { ...(original.headers || {}), Authorization: `Bearer ${newToken}` };
        return api(original);
      } catch {
        accessToken = null;
        if (onAuthFailedHandler) onAuthFailedHandler();
        return Promise.reject(err);
      }
    }

    return Promise.reject(err);
  }
);

export default api;
